import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Typography,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

const BRAND_BLUE = "#00336C";

const HEAD_CELL_SX = {
  backgroundColor: "rgba(0,51,108,0.06)",
  color: BRAND_BLUE,
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const DATA_CELL_SX = {
  fontSize: "0.875rem",
  color: BRAND_BLUE,
  borderBottom: "1px solid #edf0f5",
  py: 1,
};

const STATUS_CHIP = {
  Setup: { bg: "rgba(0,51,108,0.08)", color: BRAND_BLUE },
  Active: { bg: "rgba(100,131,21,0.12)", color: BRAND_BLUE },
  Archived: { bg: "#f0f2f5", color: BRAND_BLUE },
};
const CIRCLE_CHECKBOX_PROPS = {
  icon: <RadioButtonUncheckedIcon fontSize="small" />,
  checkedIcon: <CheckCircleIcon fontSize="small" />,
  indeterminateIcon: <RemoveCircleOutlineIcon fontSize="small" />,
};

export default function WorkspacesTable({ workspaces, selectedIds, onSelectionChange }) {
  const navigate = useNavigate();
  const selectedCount = selectedIds.size;
  const allSelected = workspaces.length > 0 && selectedCount === workspaces.length;

  const toggleWorkspace = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  const toggleAll = () => {
    onSelectionChange(allSelected ? new Set() : new Set(workspaces.map((workspace) => workspace.id)));
  };

  if (workspaces.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          color: BRAND_BLUE,
        }}
      >
        <SmartToyIcon sx={{ fontSize: 40, opacity: 0.3 }} />
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
          No workspaces yet
        </Typography>
        <Typography sx={{ fontSize: "0.82rem" }}>
          Click "Create Workspace" to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_CELL_SX, width: 48, px: 1 }} padding="checkbox">
              <Checkbox
                {...CIRCLE_CHECKBOX_PROPS}
                checked={allSelected}
                indeterminate={selectedCount > 0 && !allSelected}
                onChange={toggleAll}
                inputProps={{ "aria-label": "Select all workspaces" }}
                sx={{ color: BRAND_BLUE, "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: BRAND_BLUE } }}
              />
            </TableCell>
            {["Workspace Name", "Trade Show", "Industry", "Month / Year", "Status"].map((col) => (
              <TableCell key={col} sx={HEAD_CELL_SX}>
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {workspaces.map((ws) => {
            const chipStyle = STATUS_CHIP[ws.status] ?? STATUS_CHIP.Setup;
            return (
              <TableRow
                key={ws.id}
                hover
                onClick={() => navigate(`/trade-show-agent/workspaces/${ws.id}`)}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "rgba(0,51,108,0.03)" },
                  "&:last-child td": { borderBottom: "none" },
                }}
              >
                <TableCell sx={DATA_CELL_SX} padding="checkbox">
                  <Checkbox
                    {...CIRCLE_CHECKBOX_PROPS}
                    checked={selectedIds.has(ws.id)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => toggleWorkspace(ws.id)}
                    inputProps={{ "aria-label": `Select ${ws.workspaceName}` }}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ ...DATA_CELL_SX, fontWeight: 600, color: BRAND_BLUE }}>
                  {ws.workspaceName}
                </TableCell>
                <TableCell sx={DATA_CELL_SX}>{ws.tradeShowName || "—"}</TableCell>
                <TableCell sx={DATA_CELL_SX}>{ws.industry || "—"}</TableCell>
                <TableCell sx={DATA_CELL_SX}>{[ws.month, ws.year].filter(Boolean).join(" ") || "—"}</TableCell>
                <TableCell sx={DATA_CELL_SX}>
                  <Chip
                    label={ws.status}
                    size="small"
                    sx={{
                      backgroundColor: chipStyle.bg,
                      color: chipStyle.color,
                      fontWeight: 700,
                      fontSize: 11,
                      height: 22,
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
